'use client';

import { type ReactElement, type ReactNode, useState, useEffect } from 'react';
import { format } from 'date-fns';
import { cn, getRecommendationsCookie } from '@fshr/shared-react';
import { useTealium } from '@fshr/context/tealium-context';
import { useStickyHeaderOffset } from '@fshr/hooks';
import BookingWidgetPreview from '../../booking-widget-preview';
import BookingWidget from '../shared/product-booking-widget';

import type { PdpSection, PdpTemplateProps } from './types';
import type { AvailabilityCheckedEvent } from './product-booking-widget/types';
import LoadingDots from './product-booking-widget/loading-dots';
import getEveryPageAnalyticsVariables from './get-every-page-analytics-variables';

// The beside-aside run is the one place this template still owns a width/gutter
// wrapper directly, because it's arranging two components (content column + aside)
// side by side, not a single component's own bleed choice. Uses the shared
// spacing-wide-grid class (verified against fs-blacklight's .wide-grid-container —
// see decision 0017) instead of a hand-derived cap+padding pair; every other
// section is expected to apply spacing-wide-grid itself (or stay full-bleed) per
// the "components own their own gutter, template owns spacing between them" rule.
const SECTION_GUTTER =
	'w-full spacing-wide-grid [container-type:inline-size] [container-name:layout]';

type SectionRun = {
	layout: 'full' | 'beside-aside';
	sections: PdpSection[];
};

function groupIntoRuns(sections: PdpSection[]): SectionRun[] {
	return sections.reduce<SectionRun[]>((runs, section) => {
		const layout = section.layout === 'beside-aside' ? 'beside-aside' : 'full';
		const current = runs[runs.length - 1];

		if (current?.layout === layout) {
			current.sections.push(section);
			return runs;
		}

		return [...runs, { layout, sections: [section] }];
	}, []);
}

function renderSections(sections: PdpSection[]): ReactNode {
	return sections.map(section => (
		<section key={section.key} id={section.id}>
			{section.render()}
		</section>
	));
}

export default function PdpTemplate({
	product,
	sections,
	className = '',
	stores,
	property,
	enhancementMerchA: enhancementMerchAOverride,
}: PdpTemplateProps): ReactElement | null {
	const [isMobileWidgetOpen, setIsMobileWidgetOpen] = useState(false);
	const { trackLink } = useTealium();

	const stickyAsideOffset = useStickyHeaderOffset();

	// IAI-82: lock page scroll while mobile overlay is open (AC 2.2).
	useEffect(() => {
		document.body.style.overflow = isMobileWidgetOpen ? 'hidden' : '';
		return () => {
			document.body.style.overflow = '';
		};
	}, [isMobileWidgetOpen]);

	if (!product?.name) return null;

	const hasBookingStores =
		stores.bookingsStore &&
		stores.cartStore &&
		stores.userStore &&
		stores.globalStore &&
		stores.productAvailabilityStore;

	const enhancementAssetType = product.mediaCarousel?.find(({ type }) => type === 'VIDEO')
		? 'image_video'
		: 'image';
	const enhancementMerchA =
		enhancementMerchAOverride ??
		(product.classification === 'signature'
			? 'signature'
			: product.classification === 'curated'
				? 'curated'
				: 'basic');
	const getEveryPageVariables = () => getEveryPageAnalyticsVariables();

	const handleAvailabilityChecked = (event: AvailabilityCheckedEvent) => {
		const recoItem =
			getRecommendationsCookie()?.datalakeRecoIds?.find(
				item =>
					item?.sku?.toLowerCase() === event.enhancementSku?.toLowerCase() &&
					item.type === 'ancillary'
			) ?? null;
		const isReco = Boolean(recoItem);

		trackLink('enhancement_avail_check', {
			...getEveryPageVariables(),
			page_detail: 'select experience',
			enhancement_reco_flag: [isReco ? 'yes' : 'no'],
			enhancement_datalake_reco_id: [isReco ? (recoItem?.datalakeRecoId ?? 'na') : 'na'],
			reco_enhancement_name: [isReco ? event.enhancementName : 'na'],
			reco_enhancement_property_name: [isReco ? (recoItem?.ors ?? 'na') : 'na'],
			reco_enhancement_sku: [isReco ? event.enhancementSku : 'na'],
			enhancement_asset_types: [event.enhancementAssetType],
			enhancement_category: [event.enhancementCategory],
			enhancement_end_date: [format(event.endDate, 'yyyy-MM-dd')],
			enhancement_merch_a: [enhancementMerchA],
			enhancement_name: [event.enhancementName],
			enhancement_sku: [event.enhancementSku],
			enhancement_start_date: [format(event.startDate, 'yyyy-MM-dd')],
			enhancement_subcategory: [event.enhancementSubcategory],
			enhancement_type: [event.enhancementType],
		});
	};

	// Desktop sticky aside (hidden on mobile — the overlay takes over there).
	const aside = hasBookingStores ? (
		<BookingWidget
			userStore={stores.userStore!}
			bookingsStore={stores.bookingsStore!}
			productAvailabilityStore={stores.productAvailabilityStore!}
			cartStore={stores.cartStore!}
			property={property}
			productId={product.id}
			enhancementAssetType={enhancementAssetType}
			enhancementMerchA={enhancementMerchA}
			getEveryPageAnalyticsVariables={getEveryPageVariables}
			resortName={property.officialName}
			onAvailabilityChecked={handleAvailabilityChecked}
		/>
	) : (
		<LoadingDots />
	);

	const runs = groupIntoRuns(sections);
	const asideRunIndex = aside ? runs.findIndex(run => run.layout === 'beside-aside') : -1;

	return (
		<article className={cn('flex w-full flex-col gap-section-margin pb-section-margin', className)}>
			{!isMobileWidgetOpen && stores.productAvailabilityStore && (
				<BookingWidgetPreview
					productAvailabilityStore={stores.productAvailabilityStore}
					productId={product.id}
					onOpenFullWidget={() => setIsMobileWidgetOpen(true)}
					bookingWidgetSelector="[data-booking-widget-aside]"
				/>
			)}
			{runs.map((run, index) =>
				index === asideRunIndex ? (
					<div key={run.sections[0].key} className={SECTION_GUTTER}>
						{/* Not a gap value — the visible space here is whatever's left over
						    between a real 6-of-12 fluid grid column (content) and a fixed
						    507px widget right-aligned in the matching 6-column track on the
						    other side (aside). Verified against the Figma frame's own
						    layoutGrids (12 cols, 30px gutter, 90px/120px offset at lg/xl) —
						    a flat gap-[60px] here only ever matched one sampled viewport
						    width, not the actual fluid relationship. */}
						<div
							data-booking-section
							className="grid grid-cols-1 gap-x-fluid-grid-gutter lg:grid-cols-12"
						>
							<div className="flex min-w-0 flex-col gap-section-margin lg:col-span-6">
								{renderSections(run.sections)}
							</div>

							<div data-booking-widget-aside className="hidden lg:col-span-6 lg:block">
								<div
									data-testid="booking-widget-sticky-aside"
									className="lg:sticky lg:ml-auto lg:w-[507px]"
									style={{ top: stickyAsideOffset }}
								>
									{aside}
								</div>
							</div>
						</div>
					</div>
				) : (
					<div key={run.sections[0].key} className="flex flex-col gap-section-margin">
						{renderSections(run.sections)}
					</div>
				)
			)}

			{isMobileWidgetOpen && hasBookingStores && (
				<div
					data-testid="mobile-booking-widget-overlay"
					className="fixed inset-0 z-[60] overflow-y-auto bg-surface-default lg:hidden"
				>
					<BookingWidget
						userStore={stores.userStore!}
						bookingsStore={stores.bookingsStore!}
						productAvailabilityStore={stores.productAvailabilityStore!}
						cartStore={stores.cartStore!}
						property={property}
						productId={product.id}
						enhancementAssetType={enhancementAssetType}
						enhancementMerchA={enhancementMerchA}
						getEveryPageAnalyticsVariables={getEveryPageVariables}
						className="rounded-none border-0 min-h-screen"
						onClose={() => setIsMobileWidgetOpen(false)}
						resortName={property.officialName}
						onAvailabilityChecked={handleAvailabilityChecked}
					/>
				</div>
			)}
		</article>
	);
}
